import {
  ActionButton,
  Buttons,
  CancelButton,
  DeleteButton,
  DeleteConfirmation,
  Drawer,
  Field,
  Form,
  FormMessage,
  HelpTooltip,
} from '~/components';
import { useApi, useConfirmation, useToast, useWorkspace } from '~/contexts';
import { Formik } from 'formik';
import { useDirtyCheck, useDocumentTitle, useFeatures, useForm } from '~/hooks';
import { memberCostMethods } from '~/lookups';
import _ from 'lodash';
import React, { useMemo, useRef } from 'react';
import styled from 'styled-components';
import * as Yup from 'yup';
import { emptyStringToNull } from '~/utils';

const CheckboxRow = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
`;

const nullableBooleanOptions = [
  { value: 'true', label: 'Yes' },
  { value: 'false', label: 'No' },
];

const assistantEnabledOptions = [
  { value: 'true', label: 'Enabled' },
  { value: 'false', label: 'Disabled' },
];

const approvalModeOptions = [
  { value: 'auto', label: 'Automatic' },
  { value: 'manager', label: 'Manager' },
];

const timesheetCapacityOptions = [
  { value: 'unrestricted', label: 'No minimum requirement' },
  { value: 'timesheet', label: 'Per timesheet period' },
  { value: 'week', label: 'Per calendar week' },
];

function booleanToFieldValue(value) {
  if (value === true) return 'true';
  if (value === false) return 'false';
  return '';
}

function fieldValueToBoolean(value) {
  if (value === '') return null;
  return value === 'true';
}

function getInitialValues(template = {}) {
  return {
    name: template.name ?? '',
    description: template.description ?? '',
    employmentTypeId: template.employmentTypeId ?? '',
    costMethodId: template.costMethodId ?? '',
    isBillable: booleanToFieldValue(template.isBillable),
    defaultRateCurrency: template.defaultRateCurrency ?? null,
    securityRoleId: template.securityRoleId ?? '',
    defaultAuthProvider: template.defaultAuthProvider ?? null,
    holidayScheduleId: template.holidayScheduleId ?? '',
    practice: template.practice ?? null,
    discipline: template.discipline ?? null,
    businessUnit: template.businessUnit ?? null,
    location: template.location ?? null,
    assistantEnabled: booleanToFieldValue(template.assistantEnabled),
    timesheetCapacityPolicy: template.timesheetCapacityPolicy ?? '',
    timeOffAllowed: booleanToFieldValue(template.timeOffAllowed),
    allowedTimeOffTypes: template.allowedTimeOffTypes ?? '',
    timeOffTypes: template.timeOffTypes ?? [],
    timeOffApprovalMode: template.timeOffApprovalMode ?? '',
    internalExpenseApprovalMode: template.internalExpenseApprovalMode ?? '',
    receiveMissingTimeReminders: template.receiveMissingTimeReminders ?? false,
    unsubmittedTimesheetReminders: template.unsubmittedTimesheetReminders ?? false,
    automaticTimesheetSubmissionConfirmation: template.automaticTimesheetSubmissionConfirmation ?? false,
  };
}

export default function MemberTemplateDrawer({ template = {}, onClose, onSaved, onDeleted }) {
  const title = `${template?.id ? 'Edit' : 'New'} Member Template`;
  useDocumentTitle(title);

  const api = useApi();
  const { workspace } = useWorkspace();
  const features = useFeatures();
  const toast = useToast();
  const confirmation = useConfirmation();
  const formRef = useRef();
  const dirtyCheck = useDirtyCheck(() => formRef.current.dirty);
  const [{ status, message, isSubmitting, saved }, form] = useForm();

  const initialValues = useMemo(() => getInitialValues(template), [template]);

  function handleClose() {
    if (onClose) onClose();
  }

  return (
    <Drawer
      isOpen
      title={title}
      byline="Custom Data"
      onBeforeClose={({ setIsOpen }) => dirtyCheck(() => setIsOpen(false))}
      onClose={handleClose}>
      {(closeDrawer) => {
        const handleCloseClick = () => dirtyCheck(() => closeDrawer());

        async function handleSubmit(values, { resetForm }) {
          try {
            form.submit(formRef.current.status.action);

            const body = emptyStringToNull({
              name: values.name,
              description: values.description,
              employmentTypeId: values.employmentTypeId,
              costMethodId: values.costMethodId,
              isBillable: fieldValueToBoolean(values.isBillable),
              defaultRateCurrency: values.defaultRateCurrency,
              securityRoleId: values.securityRoleId,
              defaultAuthProviderId: values.defaultAuthProvider?.id ?? null,
              holidayScheduleId: values.holidayScheduleId,
              practiceId: values.practice?.id ?? null,
              disciplineId: values.discipline?.id ?? null,
              businessUnitId: values.businessUnit?.id ?? null,
              locationId: values.location?.id ?? null,
              assistantEnabled: fieldValueToBoolean(values.assistantEnabled),
              timesheetCapacityPolicy: values.timesheetCapacityPolicy,
              timeOffAllowed: fieldValueToBoolean(values.timeOffAllowed),
              allowedTimeOffTypes: values.allowedTimeOffTypes,
              timeOffApprovalMode: values.timeOffApprovalMode,
              internalExpenseApprovalMode: values.internalExpenseApprovalMode,
              receiveMissingTimeReminders: values.receiveMissingTimeReminders,
              unsubmittedTimesheetReminders: values.unsubmittedTimesheetReminders,
              automaticTimesheetSubmissionConfirmation: values.automaticTimesheetSubmissionConfirmation,
              timeOffTypeIds: values.timeOffTypes.map((timeOffType) => timeOffType.id),
            });

            if (body.timeOffAllowed !== true) {
              delete body.allowedTimeOffTypes;
              delete body.timeOffApprovalMode;
              delete body.timeOffTypeIds;
            } else if (body.allowedTimeOffTypes !== 'custom') {
              delete body.timeOffTypeIds;
            }

            const { data } = await api.www.workspaces(workspace.id).memberTemplates(template?.id).upsert(body);

            await onSaved(data);
            form.save(formRef.current.status.action);

            switch (formRef.current.status.action) {
              case 'new':
                resetForm({ values: getInitialValues() });
                toast.success('Member Template successfully saved.');
                break;
              case 'close':
                closeDrawer();
                break;
            }

            form.done();
          } catch ({ message }) {
            form.error({ message });
          }
        }

        async function handleDelete() {
          const confirm = await confirmation.prompt((resolve) => (
            <DeleteConfirmation resolve={resolve}>
              Are you sure you want to delete this Member Template?
            </DeleteConfirmation>
          ));
          if (!confirm) return;

          try {
            await api.www.workspaces(workspace.id).memberTemplates(template.id).delete();
            await onDeleted();
            closeDrawer();
          } catch ({ message }) {
            toast.error(message);
          }
        }

        return (
          <Formik
            innerRef={formRef}
            enableReinitialize
            initialValues={initialValues}
            onSubmit={handleSubmit}
            validateOnBlur={false}
            validateOnChange={false}
            validationSchema={Yup.object().shape({
              name: Yup.string().label('Name').max(255).trim().required(),
              description: Yup.string().label('Description').max(5000),
            })}>
            {(formik) => {
              const timeOffAllowed = fieldValueToBoolean(formik.values.timeOffAllowed);

              const handleTimeOffAllowedChange = async ({ target: { value } }) => {
                const nextValues = {
                  ...formik.values,
                  timeOffAllowed: value,
                };

                if (value !== 'true') {
                  nextValues.allowedTimeOffTypes = '';
                  nextValues.timeOffApprovalMode = '';
                  nextValues.timeOffTypes = [];
                }

                await formik.setValues(nextValues, false);

                if (value !== 'true') {
                  await formik.setFieldTouched('timeOffTypes', false, false);
                }
              };

              const handleAllowedTimeOffTypesChange = async ({ target: { value } }) => {
                const nextValues = {
                  ...formik.values,
                  allowedTimeOffTypes: value,
                };

                if (value !== 'custom') {
                  nextValues.timeOffTypes = [];
                }

                await formik.setValues(nextValues, false);

                if (value !== 'custom') {
                  await formik.setFieldTouched('timeOffTypes', false, false);
                }
              };

              const submit = (action) => {
                formik.setStatus({ action });
                formik.submitForm();
              };

              return (
                <Form>
                  {status && <FormMessage.Error>{message}</FormMessage.Error>}

                  <Form.Section title="Details">
                    <Form.Control>
                      <Field.Text autoFocus name="name" placeholder="Name" maxLength={255} />
                    </Form.Control>

                    <Form.Control>
                      <Field.TextArea name="description" placeholder="Description" maxLength={5000} />
                    </Form.Control>
                  </Form.Section>

                  <Form.Section title="General">
                    <Form.Control>
                      <Field.EmploymentTypeSelect name="employmentTypeId" placeholder="Member Type" showEmptyOption />
                    </Form.Control>

                    <Form.Control>
                      <Field.SingleSelect name="costMethodId" placeholder="Labor Cost Type" showEmptyOption>
                        {_.map(memberCostMethods, (method) => (
                          <option key={method.id} value={method.id}>
                            {method.name}
                          </option>
                        ))}
                      </Field.SingleSelect>
                    </Form.Control>

                    <Form.Control>
                      <Field.SingleSelect name="isBillable" placeholder="Billable Workspace Member" showEmptyOption>
                        {nullableBooleanOptions.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </Field.SingleSelect>
                    </Form.Control>

                    {features.multicurrency && (
                      <Form.Control>
                        <Field.WorkspaceCurrencySelect
                          name="defaultRateCurrency"
                          placeholder="Default Bill Rate Currency"
                        />
                      </Form.Control>
                    )}

                    <Form.Control>
                      <Field.SecurityRoleSelect
                        name="securityRoleId"
                        placeholder="Security Role"
                        showEmptyOption
                        initialValue={template.securityRole}
                      />
                    </Form.Control>

                    {features.businessUnits && (
                      <Form.Control>
                        <Field.BusinessUnitSelect
                          name="businessUnit"
                          placeholder="Business Unit"
                          initialValue={template.businessUnit}
                        />
                      </Form.Control>
                    )}

                    <Form.Control>
                      <Field.AuthProviderSelect name="defaultAuthProvider" placeholder="Authentication Provider" />
                    </Form.Control>

                    <Form.Control>
                      <Field.HolidayScheduleSelect
                        name="holidayScheduleId"
                        placeholder="Holiday Schedule"
                        showEmptyOption
                        holidayScheduleId={template.holidayScheduleId}
                      />
                    </Form.Control>

                    {features.practices && (
                      <Form.Control>
                        <Field.PracticeSelect name="practice" placeholder="Practice" initialValue={template.practice} />
                      </Form.Control>
                    )}

                    {features.disciplines && (
                      <Form.Control>
                        <Field.DisciplineSelect
                          name="discipline"
                          placeholder="Discipline"
                          initialValue={template.discipline}
                        />
                      </Form.Control>
                    )}

                    <Form.Control>
                      <Field.LocationSelect name="location" placeholder="Location" initialValue={template.location} />
                    </Form.Control>

                    {features.assistant && (
                      <Form.Control>
                        <Field.SingleSelect name="assistantEnabled" placeholder="Ruddr Assistant" showEmptyOption>
                          {assistantEnabledOptions.map((option) => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </Field.SingleSelect>
                      </Form.Control>
                    )}
                  </Form.Section>

                  <Form.Section title="Time Tracking">
                    {features.timesheets && (
                      <Form.Control>
                        <Field.SingleSelect
                          name="timesheetCapacityPolicy"
                          placeholder="Timesheet Capacity Setting"
                          showEmptyOption>
                          {timesheetCapacityOptions.map((option) => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </Field.SingleSelect>
                      </Form.Control>
                    )}

                    <Form.Control>
                      <Field.SingleSelect
                        name="timeOffAllowed"
                        placeholder="Ability to Track Time Off"
                        showEmptyOption
                        onChange={handleTimeOffAllowedChange}>
                        {nullableBooleanOptions.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </Field.SingleSelect>
                    </Form.Control>

                    {timeOffAllowed === true && (
                      <>
                        <Form.Control>
                          <Field.SingleSelect
                            name="allowedTimeOffTypes"
                            placeholder="Allowed Time Off Types"
                            showEmptyOption
                            onChange={handleAllowedTimeOffTypesChange}>
                            <option value="all">All time off types</option>
                            <option value="custom">Specific time off types</option>
                          </Field.SingleSelect>
                        </Form.Control>

                        {formik.values.allowedTimeOffTypes === 'custom' && (
                          <Form.Control>
                            <Field.TimeOffTypeMultiSelect name="timeOffTypes" placeholder="Time Off Types" />
                          </Form.Control>
                        )}

                        <Form.Control>
                          <Field.SingleSelect
                            name="timeOffApprovalMode"
                            placeholder="Time Off Approval"
                            showEmptyOption>
                            {approvalModeOptions.map((option) => (
                              <option key={option.value} value={option.value}>
                                {option.label}
                              </option>
                            ))}
                          </Field.SingleSelect>
                        </Form.Control>
                      </>
                    )}
                  </Form.Section>

                  {features.expenseReporting && (
                    <Form.Section title="Expenses">
                      <Form.Control>
                        <Field.SingleSelect
                          name="internalExpenseApprovalMode"
                          placeholder="Internal Expense Approval"
                          showEmptyOption>
                          {approvalModeOptions.map((option) => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </Field.SingleSelect>
                      </Form.Control>
                    </Form.Section>
                  )}

                  <Form.Section title="Notifications">
                    <Form.Control>
                      <CheckboxRow>
                        <Field.Checkbox name="receiveMissingTimeReminders" label="Missing Time Reminders" />
                        <HelpTooltip message="Sends this member a reminder email when they have not entered enough hours for the period." />
                      </CheckboxRow>
                    </Form.Control>

                    {features.timesheets && (
                      <>
                        <Form.Control>
                          <CheckboxRow>
                            <Field.Checkbox
                              name="unsubmittedTimesheetReminders"
                              label="Timesheet Submission Reminders"
                            />
                            <HelpTooltip message="Sends this member a reminder email when they have one or more unsubmitted timesheets in the past 5 weeks." />
                          </CheckboxRow>
                        </Form.Control>

                        <Form.Control>
                          <CheckboxRow>
                            <Field.Checkbox
                              name="automaticTimesheetSubmissionConfirmation"
                              label="Automatic Timesheet Submission Confirmation"
                            />
                            <HelpTooltip message="Sends this member a confirmation email when one of their timesheets is automatically submitted." />
                          </CheckboxRow>
                        </Form.Control>
                      </>
                    )}
                  </Form.Section>

                  <Drawer.Actions>
                    {template.id && <DeleteButton onClick={handleDelete}>Delete</DeleteButton>}

                    <Buttons align="right">
                      <CancelButton onClick={handleCloseClick}>Close</CancelButton>

                      {!template.id && (
                        <ActionButton
                          isLoading={isSubmitting === 'new'}
                          ok={saved === 'new'}
                          type="submit"
                          onClick={() => submit('new')}>
                          Save &amp; New
                        </ActionButton>
                      )}

                      <ActionButton
                        isLoading={isSubmitting === 'close'}
                        ok={saved === 'close'}
                        type="submit"
                        onClick={() => submit('close')}>
                        Save &amp; Close
                      </ActionButton>
                    </Buttons>
                  </Drawer.Actions>
                </Form>
              );
            }}
          </Formik>
        );
      }}
    </Drawer>
  );
}
